import { formatIDR } from "@marketplace/shared/utils";
import {
  Button,
  ConditionalRendering,
  Text,
  UnstyledButton
} from "@marketplace/ui";
import { type DataTableColumn } from "@marketplace/ui/data-table";
import { getRouteApi } from "@tanstack/react-router";
import { useSetAtom } from "jotai";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MdOutlineTextSnippet } from "react-icons/md";

import { formatWithOffset } from "@/utils";

import { voucherApprovalModalAtom } from "../atoms";
import { VoucherListTableActions } from "../components/voucher-list-table-actions";
import type { VoucherListItem } from "../types";

const route = getRouteApi("/vouchers/");

const formatDate = (date?: string) => {
  if (!date) return "-";

  return formatWithOffset(date, "dd MMMM yyyy");
};

export const useVoucherListTableColumns = () => {
  const { page } = route.useSearch();
  const { t } = useTranslation("voucher");
  const setVoucherApprovalModal = useSetAtom(voucherApprovalModalAtom);

  const voucherListTableColumns = useMemo<DataTableColumn<VoucherListItem>[]>(
    () => [
      {
        accessor: "no",
        title: "No",
        render: (_, index) => (page - 1) * 10 + index + 1,
        width: 60
      },
      {
        accessor: "voucher_code",
        title: t("table.voucherList.columns.voucherCode"),
        width: 120
      },
      {
        accessor: "product_name",
        title: t("table.voucherList.columns.productName"),
        width: 150
      },
      {
        accessor: "product_package_name",
        title: t("table.voucherList.columns.packageName"),
        width: 150
      },
      {
        accessor: "variant_name",
        title: t("table.voucherList.columns.variant"),
        render: ({ variant_name }) => variant_name || "-",
        width: 120
      },
      {
        accessor: "price_discount",
        title: t("table.voucherList.columns.discountAmount"),
        render: ({ price_discount }) =>
          price_discount ? formatIDR(price_discount) : "-",
        width: 120
      },
      {
        accessor: "validity",
        title: t("table.voucherList.columns.validityPeriod"),
        render: ({ valid_start_date, valid_end_date }) =>
          `${formatDate(valid_start_date)} - ${formatDate(valid_end_date)}`,
        width: 200
      },
      {
        accessor: "is_used",
        title: t("table.voucherList.columns.status"),
        width: 150,
        render: (voucher) => {
          return (
            <div>
              <ConditionalRendering condition={voucher.status !== "on_review"}>
                <UnstyledButton
                  className={`${voucher.status === "active" ? "text-[#45B527] bg-[#E5FFDE]" : "text-[#8B1515] bg-[#FFE7E7]"} cursor-pointer  rounded-[4px] py-1 w-24 flex items-center justify-center `}
                  onClick={() =>
                    setVoucherApprovalModal({
                      visible: true,
                      data: {
                        voucherId: voucher.id
                      }
                    })
                  }
                >
                  <Text>
                    {voucher.status === "active" &&
                      t("table.voucherList.status.active")}
                    {voucher.status === "inactive" &&
                      t("table.voucherList.status.inactive")}
                    {voucher.status === "rejected" &&
                      t("table.voucherList.status.rejected")}
                    {voucher.status === "expired" &&
                      t("table.voucherList.status.expired")}
                  </Text>
                </UnstyledButton>
              </ConditionalRendering>
              <ConditionalRendering condition={voucher.status === "on_review"}>
                <Button
                  variant="secondary"
                  rightSection={<MdOutlineTextSnippet size={14} />}
                  onClick={() =>
                    setVoucherApprovalModal({
                      visible: true,
                      data: {
                        voucherId: voucher.id
                      }
                    })
                  }
                >
                  {t("table.voucherList.status.review")}
                </Button>
              </ConditionalRendering>
            </div>
          );
        }
      },
      {
        accessor: "actions",
        title: "",
        render: (record) => (
          <div className="flex justify-end">
            <VoucherListTableActions voucherData={record} />
          </div>
        ),
        width: 80
      }
    ],
    [t, page, setVoucherApprovalModal]
  );

  return { columns: voucherListTableColumns };
};
